<script setup>
import QuickFilter from 'vue-elements/comparison-inputs/QuickFilter.vue';
import Icon from 'vue-elements/icon/Icon.vue';
import smoothscroll from 'smoothscroll-polyfill';
import {
  computed, onMounted, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import isEqual from 'lodash/isEqual';
import TransitionProductCards from 'vue-elements/transitions/TransitionProductCards.vue';
import PostalBox from 'vue-elements/comparison-inputs/PostalBox.vue';
import { reduce } from 'lodash';
import InternetProductCard from '../components/product/InternetProductCard.vue';
import FilterBar from '../components/FilterBar.vue';
import Loader from '../components/Loader.vue';
import SpeedAssistant from '../components/SpeedAssistant.vue';
import mijnkeuzeStore from '../../../sections/mijnKeuze/store/store';
import SaveInternetComparison from '../components/SaveInternetComparison.vue';
import ModalHook from '../../../../abstracts/elements/modal/_shared/hook/modalHook.vue';
import ButtonIconRight from '../../../../abstracts/elements/button/icon-right/button-icon-right.vue';
import ComparisonTip from 'vue-elements/comparison-elements/ComparisonTip.vue';
import {
  sortingOptions,
  productTypes,
} from '../lib/filterOptions.js';
import ButtonNoIcon from '../../../../abstracts/elements/button/no-icon/button-no-icon.vue';
import ButtonIconLeft from '../../../../abstracts/elements/button/icon-left/button-icon-left.vue';
import { useInternetComparisonStore } from '../stores/internetComparison.js';
import { changedKeys } from '../../../vue-elements/comparison-lib/url';
import { logAnalyticsEvent } from '../../../../assets/js/helpers/bananas';
import FrankModal from '../../frank/FrankModal.vue';
import { useFrankStore } from '../../frank/store/frankStore.js';
import SelectInput from '../../../vue-elements/input/SelectInput.vue';

/* Router */
const router = useRouter();

/* Store implementation */
const comparisonStore = useInternetComparisonStore();

/*
 * Props
*/
const props = defineProps([
  'urlPostcode',
  'urlNumber',
  'urlNumberExtension',
  'urlFilters',
]);

// Emits:
const emit = defineEmits(['updateAddressBar']);

// Fiberconnection

const onlyFiberFilterActive = computed(() => {
  const { fiber, dsl, cable, mobileData } = comparisonStore.filters;
  return fiber === true && dsl === false && cable === false && mobileData === false;
});


/*
 * General helpers
*/
onMounted(async () => {
  if (props.urlPostcode !== comparisonStore.postalCode) {
    comparisonStore.postalCode = props.urlPostcode;
    comparisonStore.houseNumber = props.urlNumber;
    comparisonStore.houseNumberAddition = props.urlNumberExtension;
  }

  // Load filters from URL: this will trigger a load.
  if (props.urlFilters) {
    comparisonStore.filters = props.urlFilters;
  } else {
    comparisonStore.load();
  }

  // await comparisonStore.load();

  smoothscroll.polyfill();
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
});

comparisonStore.firstLoading = true;

const sortingIcon = computed(() => {
  switch (comparisonStore.filters.sort) {
    case 'price':
    case 'price_monthly':
      return 'x-fa-sort-amount-arrow-down';
    case 'speed':
      return 'x-fa-sort-amount-down';
    default:
      return '';
  }
});

/*
 * Frank implementation
*/
const frankStore = useFrankStore();

onMounted(() => {
  frankStore.setFrankComparer('internet');
});
const setFrankProduct = (product) => {
  frankStore.setFrankProduct(product);
};
/* End frank */

/*
 * Speed assistant
*/
const speedAssist = ref(null);

const closeSpeedAssistant = () => {
  comparisonStore.speedAssistant = false;
};
/* End Speed assistant */

/*
 * Address implementation
*/
onMounted(() => {
  comparisonStore.setCompareLocation(props);

  comparisonStore.firstLoading = true;
});
const editAddress = () => {
  comparisonStore.forceAddressDisplay = true;
};

const showExtensions = computed(() => (
  comparisonStore.compareLocation !== null
  && comparisonStore.compareLocation.data.address_found === false
  && comparisonStore.compareLocation.data.alt_extensions.length > 0
));

const addressDisplayReady = computed(() => comparisonStore.addressReady && comparisonStore.compareLocation.data.address_found);

/* End address implementation */

// json version of filter object for events
const jsonFilters = computed(() => JSON.stringify(comparisonStore.filters));
watch(jsonFilters, async (newFilters, oldFilters) => {
  const jsonOldFilters = JSON.parse(oldFilters);
  const jsonNewFilters = JSON.parse(newFilters);

  await comparisonStore.load();

  const changedFilters = reduce(
    jsonOldFilters,
    (result, value, key) => (isEqual(value, jsonNewFilters[key]) ? result : result.concat(key)),
    [],
  );

  changedFilters.forEach((filterName) => {
    let filterValue = null;

    if (comparisonStore.filters[filterName]) {
      filterValue = comparisonStore.filters[filterName];
    }

    logAnalyticsEvent('compare-internet.filter', {
      filter_name: filterName,
      filter_value: filterValue,
    });
  });
});

const updateURL = () => {
  // Construct query parameters:
  const query = comparisonStore.buildQuery;

  // Avoid duplicate navigation:
  if (router.currentRoute.value && (router.currentRoute.value.name !== 'compare' || !isEqual(router.currentRoute.value.query, query))) {
    // Push when changing other parameters, replace when changing last parameter.
    // Check which filter parameter was changed and whether this is different from the last changed parameter.
    const lastChangedFilters = changedKeys(router.currentRoute.value.query, query);

    let push = false;
    if (lastChangedFilters.length === 1) {
      if (comparisonStore.lastChangedFilter == null || lastChangedFilters[0] !== comparisonStore.lastChangedFilter) {
        push = true;
      }

      [comparisonStore.lastChangedFilter] = lastChangedFilters;
    } else {
      push = true;
      [comparisonStore.lastChangedFilter] = lastChangedFilters;
    }
    if (push) {
      router.push({
        name: 'compare',
        query,
      });
    } else {
      router.replace({
        name: 'compare',
        query,
      });
    }
  }
};

watch(() => comparisonStore.filters, () => {
  updateURL();
  comparisonStore.load();
}, { deep: true });

const updateFilters = (filters) => {
  comparisonStore.filters = filters;
};

watch(() => comparisonStore.compareLocation, (newCompareLocation, oldCompareLocation) => {
  if (props.urlFilters.minSpeed === 0) {
    comparisonStore.setRealisticSpeedDefault();
  }

  if (
    newCompareLocation
    && newCompareLocation.data.address_found
    && (!oldCompareLocation || !oldCompareLocation.data.address_found)
  ) {
    const locationStats = {
      max_speed: comparisonStore.maxSpeed,
      max_speed_dsl: newCompareLocation.data.max_dsl || 0,
      max_speed_cable: newCompareLocation.data.max_cable || 0,
      max_speed_fiber: newCompareLocation.data.max_fiber || 0,
      city: newCompareLocation.data.city,
    };

    // Assuming logAnalyticsEvent() is a method in your component
    logAnalyticsEvent('compare-internet.location-speed', locationStats);
  } else if (newCompareLocation && !newCompareLocation.data.address_found) {
    logAnalyticsEvent('compare-internet.address-not-found');
  }
  updateURL();
});

const openSaveComparisonModal = () => {
  document.querySelector('input#save-comparison')
    .click();
};
</script>

<template>
  <div class="comparison-overview-view">
    <div class="comparison-overview">

      <!-- <div
        class="compare-promotion-bar promotion-banner"
      >
        <div class="text">
          Deze maand tot wel 50% korting bij internet providers
        </div>
      </div> -->
      <div class="inner compare-overview-container container-xl">
        <speed-assistant ref="speedAssist" @speed="comparisonStore.filters.minSpeed = $event"
          @close="closeSpeedAssistant" />
        <div class="comparison-sorters-container">
          <Transition name="filter-bar">
            <div v-show="comparisonStore.initialLoadDone" class="comparison-sorters">
              <div class="sorters-row header">
                <div class="select-field custom-icon">
                  <select id="productTypes" v-model="comparisonStore.filters.productType" class="select-element empty"
                    name="productTypes">
                    <option v-for="productTypeOption in productTypes" :key="productTypeOption.value"
                      :value="productTypeOption.value">
                      {{ productTypeOption.label }}
                    </option>
                  </select>
                  <icon icon="x-fa-chevron-down" class="" size="sm" />
                </div>
                <div class="link filter-button" @click="comparisonStore.showMobileFilters = true">
                  Filters
                  <icon icon="slider-light" />
                </div>
              </div>
            </div>
          </Transition>
        </div>
        <Transition name="filter-bar">
          <div v-show="comparisonStore.initialLoadDone" class="compare-sort-bar mobile">
            <div class="sort-item">
              <div class="select-field custom-icon">
                <select id="sortingOptions" v-model="comparisonStore.filters.sort" class="select-element empty"
                  :class="comparisonStore.filters.sort" name="sortingOptions">
                  <option v-for="option in sortingOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
                <icon :icon="sortingIcon" size="sm" />
              </div>
            </div>
          </div>
        </Transition>

        <div class="comparison-body" :class="{ 'is-loading': comparisonStore.loading }">
          <Transition name="filter-bar" appear>
            <Filter-bar :filters="comparisonStore.filters" :show-extensions="showExtensions"
              :location="comparisonStore.compareLocation" :loading="comparisonStore.loading"
              :address-ready="comparisonStore.addressReady" :address-display-ready="addressDisplayReady"
              :show="comparisonStore.showMobileFilters" :total-products="comparisonStore.totalProducts"
              :products="comparisonStore.products" :saved-comparison="comparisonStore.savedComparison"
              :signed-in="comparisonStore.signedIn" @filter="updateFilters"
              @speedassistant="comparisonStore.speedAssistant = true" @postal="comparisonStore.doPostal($event)"
              @editAddress="editAddress()" @hideFilters="comparisonStore.showMobileFilters = false" />
          </Transition>

          <div class="floating-panel" :class="{ expand: comparisonStore.showMobileFilters }"
            @click="comparisonStore.showMobileFilters = false">
            <button-icon-right type="cta" class="apply-filter-button"
              :class="{ 'is-loading-state': comparisonStore.loading }" @click.native="$emit('close')">
              <template v-if="!comparisonStore.loading" #label>
                Toon {{ comparisonStore.totalProducts }}
                <template v-if="comparisonStore.totalProducts === 1">
                  abonnement
                </template>
                <template v-else>
                  abonnementen
                </template>
              </template>
              <template v-else #label>
                <loader />
              </template>
            </button-icon-right>
          </div>

          <div class="compare-container">
            <!-- Sorteer bar -->
            <Transition name="fade">
              <div v-show="comparisonStore.initialLoadDone" class="compare-sort-bar desktop">
                <div class="sort-item">
                  <div>Vergelijk:</div>
                  <SelectInput id="productTypes" v-model="comparisonStore.filters.productType" name="productTypes"
                    :options="productTypes" />
                </div>
                <div class="sort-item">
                  <div>Sorteer op:</div>
                  <SelectInput id="sortingOptions" v-model="comparisonStore.filters.sort" name="sortingOptions"
                    :options="sortingOptions" :icon="sortingIcon" />
                </div>
              </div>
              <!---->
            </Transition>

            <!-- Product container -->
            <div class="products_container" :class="{ inactive: !comparisonStore.addressReady }">
              <div class="comparison-postal">
                <div v-if="!comparisonStore.loading && comparisonStore.error" class="product no-products loading-error">
                  <icon icon="x-fa-exclamation-triangle" />

                  <strong>Door een fout kunnen we geen informatie laten zien.</strong>
                  {{ comparisonStore.errorMessage }}
                  <div class="error-button">
                    <button-icon-left type="cta" label="Probeer opnieuw" icon="x-fa-redo"
                      @click.native="comparisonStore.load()" />
                  </div>
                </div>

                <Transition name="fade" mode="out-in">
                  <div v-if="!comparisonStore.addressReady && !comparisonStore.firstLoading && !showExtensions"
                    class="postal-box-mobile">
                    <div class="panel">
                      <postal-box :location="comparisonStore.compareLocation" :loading="comparisonStore.loading"
                        @postal="comparisonStore.doPostal($event)" />
                    </div>
                  </div>
                </Transition>

                <Transition name="fade" mode="out-in">
                  <div v-if="showExtensions" class="extension-container compare-ready">
                    Kies een van de toevoegingen voor postcode
                    <strong>{{ comparisonStore.compareLocation.data.postCode }}</strong> met huisnummer
                    <strong>{{ comparisonStore.compareLocation.data.number }}</strong>:

                    <div class="extension-picker">
                      <button-no-icon v-for="extension in comparisonStore.compareLocation.data['alt_extensions']"
                        :key="extension" type="cta"
                        :label="extension === null ? `${comparisonStore.compareLocation.data.number} (zonder toevoeging)` : `${comparisonStore.compareLocation.data.number} ${extension} `"
                        @click.native="
                          comparisonStore.pickExtension(
                            comparisonStore.compareLocation.data.postCode,
                            comparisonStore.compareLocation.data.number,
                            extension
                          )" />
                    </div>
                  </div>
                </Transition>
              </div>
              <div class="products" :class="{ 'initial-load': !comparisonStore.initialLoadDone }">
                <Transition name="loader-fade">
                  <div v-if="comparisonStore.loading && comparisonStore.products.length === 0"
                    class="loading-indicator">
                    <loader />
                    <div class="loading-text">
                      Internetabonnementen laden...
                    </div>
                  </div>
                </Transition>
                <Transition name="loader-fade">
                  <comparison-tip
                    v-if="!comparisonStore.error && !comparisonStore.loading && !comparisonStore.addressReady"
                    class="postal-needed warning" :style="{ 'transition-delay': '.3s' }" icon="x-fa-arrow-left"
                    :show-close="false">
                    <template #message>
                      Vergelijk met de juiste prijzen door je
                      <strong>postcode</strong> en <strong>huisnummer</strong> in te
                      vullen.
                    </template>
                  </comparison-tip>
                </Transition>
                <div
                  v-if="comparisonStore.products && comparisonStore.products.length === 0 && onlyFiberFilterActive && !comparisonStore.loading"
                  class="product">
                  <div class="no-products">
                    <span class="error-message">Er is helaas nog geen glasvezel beschikbaar op jouw adres.</span>
                    <span class="sub-error-message">Met de <a href="/internet/wanneer-krijg-ik-glasvezel">glasvezel
                        check</a> van
                      Keuze.nl schrijf je je in voor updates over de aanleg van glasvezel bij jou thuis.</span>
                    <span class="error-button">
                      <button-no-icon type="cta" label="Vergelijk alle beschikbare abonnementen"
                        @click.native="comparisonStore.resetFilters()" />

                    </span>
                  </div>
                </div>
                <div
                  v-else-if="comparisonStore.products && comparisonStore.products.length === 0 && !comparisonStore.loading"
                  class="product">
                  <div class="no-products">
                    <span class="error-message">Er zijn geen abonnementen gevonden die aan je filters
                      voldoen.</span>
                    <span class="error-button">
                      <button-no-icon type="cta" label="Filters terugzetten"
                        @click.native="comparisonStore.resetFilters()" />

                    </span>
                  </div>
                </div>
                <TransitionProductCards>
                  <internet-product-card v-for="product in comparisonStore.products"
                    v-if="(!comparisonStore.error && comparisonStore.paginating) || !comparisonStore.error"
                    :key="product.base_id" :product="product" :location="comparisonStore.compareLocation"
                    :sort="comparisonStore.filters.sort" @productClick="setFrankProduct(product)" />
                </TransitionProductCards>
                <Transition name="loader-fade">
                  <button
                    v-if="comparisonStore.hasMoreProducts && comparisonStore.initialLoadDone && !comparisonStore.loading"
                    class="button-element icon-left ghost more"
                    @click.native="$event.target.blur(); comparisonStore.loadMore();">
                    <span v-if="comparisonStore.paginating" class="button-loader">
                      <loader />
                    </span>
                    <template v-else>
                      <icon icon="x-fa-plus" />
                      <span class="button-label">
                        {{
                          comparisonStore.moreProductCount > 1 ? `${comparisonStore.moreProductCount} abonnementen` :
                            `${comparisonStore.moreProductCount} abonnement`
                        }}</span>
                    </template>
                  </button>
                </Transition>
              </div>
            </div>
          </div>
        </div>
        <FrankModal @save="openSaveComparisonModal" />
        <save-internet-comparison :location="comparisonStore.compareLocation" :products="comparisonStore.products"
          :filters="comparisonStore.filters" @savedComparison="comparisonStore.setSavedComparison($event)" />
      </div>
    </div>
  </div>
</template>
